import React, { useState, useEffect } from 'react';
import { useTheme } from '@mui/material/styles';

const STORAGE_KEY = 'alphanetix_cookie_consent';

// Update Google Consent Mode v2 based on user choice
function updateConsent(granted) {
  if (typeof window.gtag !== 'function') return;
  const value = granted ? 'granted' : 'denied';
  window.gtag('consent', 'update', {
    analytics_storage: value,
    ad_storage: value,
    ad_user_data: value,
    ad_personalization: value,
  });
}

export default function CookieConsent() {
  const theme = useTheme();
  const isLight = theme.palette.mode === 'light';
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (!saved) {
      // Small delay so it doesn't flash during initial page load / splash
      const t = setTimeout(() => setVisible(true), 1500);
      return () => clearTimeout(t);
    }
    // Re-apply saved preference on every page load so GA4 is properly configured
    updateConsent(saved === 'granted');
  }, []);

  const handleAccept = () => {
    localStorage.setItem(STORAGE_KEY, 'granted');
    updateConsent(true);
    setVisible(false);
  };

  const handleDecline = () => {
    localStorage.setItem(STORAGE_KEY, 'denied');
    updateConsent(false);
    setVisible(false);
  };

  if (!visible) return null;

  const bg = isLight ? '#ffffff' : '#0f1e35';
  const border = isLight ? '1px solid #e2e8f0' : '1px solid rgba(56,189,248,0.15)';
  const textColor = isLight ? '#1e293b' : '#cbd5e1';
  const mutedColor = isLight ? '#64748b' : '#94a3b8';
  const linkColor = isLight ? '#4f46e5' : '#38bdf8';
  const acceptBg = isLight
    ? 'linear-gradient(135deg, #1e293b 0%, #334155 100%)'
    : 'linear-gradient(135deg, #10b981 0%, #0ea5e9 100%)';
  const declineBg = 'transparent';
  const declineBorder = isLight ? '1px solid #cbd5e1' : '1px solid rgba(148,163,184,0.3)';

  return (
    <div
      role="dialog"
      aria-label="Cookie consent"
      aria-live="polite"
      style={{
        position: 'fixed',
        bottom: 24,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 99999,
        width: 'min(680px, calc(100vw - 32px))',
        background: bg,
        border,
        borderRadius: 14,
        boxShadow: isLight
          ? '0 8px 32px rgba(0,0,0,0.12)'
          : '0 8px 40px rgba(0,0,0,0.5), 0 0 0 1px rgba(56,189,248,0.08)',
        padding: '20px 24px',
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
        animation: 'cookieSlideUp 0.35s cubic-bezier(0.34,1.56,0.64,1) both',
      }}
    >
      <style>{`
        @keyframes cookieSlideUp {
          from { opacity: 0; transform: translateX(-50%) translateY(24px); }
          to   { opacity: 1; transform: translateX(-50%) translateY(0); }
        }
      `}</style>

      {/* Text */}
      <div>
        <p style={{ margin: '0 0 6px', fontSize: 14, fontWeight: 600, color: textColor, fontFamily: 'Inter, sans-serif' }}>
          We use cookies
        </p>
        <p style={{ margin: 0, fontSize: 13, lineHeight: 1.6, color: mutedColor, fontFamily: 'Inter, sans-serif' }}>
          We use Google Analytics to understand how visitors interact with our site and improve your experience.
          No advertising cookies are used.{' '}
          <a
            href="/privacy-policy"
            style={{ color: linkColor, textDecoration: 'underline', fontWeight: 500 }}
          >
            Privacy Policy
          </a>
        </p>
      </div>

      {/* Buttons */}
      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
        <button
          onClick={handleDecline}
          style={{
            padding: '8px 18px',
            borderRadius: 8,
            border: declineBorder,
            background: declineBg,
            color: mutedColor,
            fontSize: 13,
            fontWeight: 500,
            fontFamily: 'Inter, sans-serif',
            cursor: 'pointer',
            transition: 'color 0.2s, border-color 0.2s',
          }}
          onMouseEnter={e => { e.currentTarget.style.color = textColor; }}
          onMouseLeave={e => { e.currentTarget.style.color = mutedColor; }}
        >
          Decline
        </button>
        <button
          onClick={handleAccept}
          style={{
            padding: '8px 20px',
            borderRadius: 8,
            border: 'none',
            background: acceptBg,
            color: '#ffffff',
            fontSize: 13,
            fontWeight: 600,
            fontFamily: 'Inter, sans-serif',
            cursor: 'pointer',
            boxShadow: isLight ? '0 2px 8px rgba(0,0,0,0.15)' : '0 2px 12px rgba(16,185,129,0.3)',
            transition: 'opacity 0.2s',
          }}
          onMouseEnter={e => { e.currentTarget.style.opacity = '0.88'; }}
          onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
        >
          Accept Analytics
        </button>
      </div>
    </div>
  );
}
